/**
 * Shared utilities for seat data transformation
 * 
 * Used by portfolio page and profile page to group and transform seat data.
 */

import type { CoinSeatsData, SeatPosition } from './coin-seats-card';
import { resolveBalanceAndRunway } from '@/lib/tax';
import { grossRewardsPerTile, resolveRewardTokenInfo, type RewardKind } from '@/lib/rewards';
import { safeBigInt } from '@/lib/bigint';

export interface EarningsResult {
  amount: bigint;
  kind: RewardKind;
  symbol?: string;
  decimals: number;
}

/**
 * Raw seat data from API (common fields used by both pages)
 */
export interface RawSeatData {
  id?: string;
  poolId?: string;
  seatIndex: number;
  selfAssessedPrice: string;
  balance: string;
  lastUpdateTime?: number;
  claimedAt?: number;
  lastPriceChangeTime?: number;
  pool?: {
    id: string;
    name?: string;
    symbol?: string;
    imageUrl?: string;
    memecoinAddress?: string;
    managerAddress?: string;
    /** Grid creator address (lowercased). Lets consumers detect
     *  primary-mint creator seats — `holder == creatorAddress` with a
     *  zero deposit means the seat hasn't been claimed/sold yet, so it
     *  pays no tax and shouldn't appear with forfeit warnings in
     *  "your tiles" surfaces (mirrors the board's `MySeatsCard`
     *  filter). */
    creatorAddress?: string;
    floorPrice?: string;
    totalFeesDistributed?: string;
    totalRewardsEth?: string;
    activeSeatCount?: number;
    isGraduated?: boolean;
    createdAt?: number;
    isV2Grid?: boolean;
    v2YieldToken?: string;
    v2YieldTokenDecimals?: number;
    taxRateBps?: number;
    taxToken?: string;
    taxTokenDecimals?: number;
    totalSeats?: number;
    yield7d?: string;
  };
  metrics?: {
    currentBalance?: string;
    timeUntilForfeiture?: number;
  };
}

/**
 * Calculate estimated weekly earnings per seat.
 *
 * Delegates to the shared rewards module. Returns the gross per-tile value
 * with metadata about whether it's in USDC 6-dec ('usd') or raw token
 * units ('native'), so the UI can format each appropriately.
 */
export function calculateEstimatedWeeklyEarnings(
  pool: RawSeatData['pool'],
  liveAvgEarningsUsd?: bigint,
  rewardPriceUsd?: number,
  totalRewardsEth7d?: string
): EarningsResult {
  const usdDefault: EarningsResult = { amount: 0n, kind: 'usd', decimals: 6 };
  if (liveAvgEarningsUsd !== undefined && liveAvgEarningsUsd > 0n) {
    return { amount: liveAvgEarningsUsd, kind: 'usd', decimals: 6 };
  }
  if (!pool) return usdDefault;

  const decimals = pool.isV2Grid ? (pool.v2YieldTokenDecimals ?? 18) : 18;
  const yieldAddr = pool.isV2Grid
    ? (pool.v2YieldToken || '').toLowerCase()
    : undefined;
  const tokenPricesMap = yieldAddr && rewardPriceUsd
    ? { [yieldAddr]: rewardPriceUsd }
    : undefined;
  const rewardToken = resolveRewardTokenInfo(
    pool.isV2Grid ? pool.v2YieldToken : undefined,
    decimals,
    rewardPriceUsd || 0,
    tokenPricesMap,
  );

  const result = grossRewardsPerTile({
    // `safeBigInt` here matters for the same reason the wrapping hooks
    // already use it: this helper is called from `groupSeatsByCoin`,
    // which runs inside `useLobbySeatHoldings` — and that hook backs
    // `MobileBoardRail` (mounted in `AppShell.MobileChromeTop`,
    // *outside* the lobby's `error.tsx` boundary). A bare
    // `BigInt(...)` throw here would tear down the entire mobile
    // chrome, with no error boundary to catch it, and the user
    // would see Next.js's bare "Application error" fallback. The
    // helper degrades to `0n` instead, which every downstream
    // consumer already treats as "no rewards".
    totalRewardsRaw: safeBigInt(pool.totalRewardsEth),
    rewards7dRaw: safeBigInt(totalRewardsEth7d),
    poolCreatedAt: pool.createdAt || 0,
    rewardToken,
    tileCount: 100,
  });

  return {
    amount: result.amount,
    kind: result.kind,
    symbol: result.symbol,
    decimals: result.decimals,
  };
}

function resolveRewardPrice(pool: RawSeatData['pool'], ethPriceUsd?: number, tokenPrices?: Record<string, number>): number {
  if (!pool?.isV2Grid) return ethPriceUsd || 0;
  if (!tokenPrices || !pool.v2YieldToken) return 0;
  const WETH_ADDR = '0x4200000000000000000000000000000000000006';
  const ZERO_ADDR = '0x0000000000000000000000000000000000000000';
  const addr = (!pool.v2YieldToken || pool.v2YieldToken === ZERO_ADDR) ? WETH_ADDR : pool.v2YieldToken;
  if (addr === WETH_ADDR) return ethPriceUsd || tokenPrices[WETH_ADDR] || 0;
  return tokenPrices[addr] || 0;
}

/**
 * Transform a raw seat into a SeatPosition with computed metrics
 *
 * @param ethPriceUsd ETH price (used for V1 pools)
 * @param tokenPrices Map of token address -> USD price (used for V2 yield tokens)
 * @param options.includeForfeited When true, return a zero-balance
 *   `isForfeited=true` position for tax-depleted seats instead of `null`.
 *   Defaults to `false` so portfolio/profile pages keep filtering them
 *   out — only the lobby manage modal opts in (it needs to surface the
 *   header-chip seats so users can top up or abandon their zombies).
 */
export function transformToSeatPosition(
  seat: RawSeatData,
  liveAvgEarningsUsd?: bigint,
  ethPriceUsd?: number,
  tokenPrices?: Record<string, number>,
  rewards7dMap?: Map<string, string>,
  options?: { includeForfeited?: boolean },
): SeatPosition | null {
  // `safeBigInt` (not bare `BigInt`) — see the docstring on
  // `calculateEstimatedWeeklyEarnings` above for the full rationale:
  // this transformer runs inside `useLobbySeatHoldings`, which sits
  // outside the route-group `error.tsx`, so any synchronous throw
  // here surfaces as Next.js's "Application error" fallback instead
  // of a recoverable boundary. A malformed indexer field (scientific
  // notation, decimal tail, etc.) degrades to `0n` and the seat just
  // renders with zero price / balance — strictly better than killing
  // the chrome.
  const price = safeBigInt(seat.selfAssessedPrice);
  const rawBalance = safeBigInt(seat.balance);
  const lastUpdate = Number(seat.lastUpdateTime || 0);
  const taxBps = safeBigInt(seat.pool?.taxRateBps, 500n);
  const { balance, runwaySeconds: runway } = resolveBalanceAndRunway(
    rawBalance, price, taxBps, lastUpdate, seat.metrics,
  );

  // Tax-depleted seats ("zombies") still show up in the indexer as the
  // viewer's because nobody has called `pokeTax` to formally forfeit
  // them yet. Hiding them entirely makes the lobby header tiles chip
  // count diverge from the manage modal — the chip uses `isActive`
  // (indexer truth) while the modal used to filter on `balance > 0n`.
  // `includeForfeited` lets the modal show them so users can actually
  // top up / abandon instead of seeing an empty list.
  if (balance <= 0n) {
    if (!options?.includeForfeited) return null;
    const weeklyTaxForfeit = (price * taxBps) / 10000n;
    return {
      seatIndex: seat.seatIndex,
      selfAssessedPrice: price,
      balance: 0n,
      weeklyEarnings: 0n,
      weeklyTax: weeklyTaxForfeit,
      runwaySeconds: 0,
      heldSince: seat.claimedAt || seat.lastUpdateTime || Math.floor(Date.now() / 1000),
      isInCooldown: false,
      cooldownMinutesRemaining: 0,
      isForfeited: true,
    };
  }

  const weeklyTax = (price * taxBps) / 10000n;
  
  const rewardPrice = resolveRewardPrice(seat.pool, ethPriceUsd, tokenPrices);
  const poolId = seat.pool?.id || seat.poolId || '';
  const yield7d = rewards7dMap?.get(poolId) ?? seat.pool?.yield7d;
  const earnings = calculateEstimatedWeeklyEarnings(seat.pool, liveAvgEarningsUsd, rewardPrice, yield7d);

  const COOLDOWN_SECONDS = 20 * 60;
  const now = Math.floor(Date.now() / 1000);
  const lastPriceChange = seat.lastPriceChangeTime || 0;
  const isInCooldown = lastPriceChange > 0 && now < (lastPriceChange + COOLDOWN_SECONDS);
  const cooldownMinutesRemaining = isInCooldown 
    ? Math.ceil((lastPriceChange + COOLDOWN_SECONDS - now) / 60) 
    : 0;

  return {
    seatIndex: seat.seatIndex,
    selfAssessedPrice: price,
    balance,
    weeklyEarnings: earnings.amount,
    weeklyTax,
    runwaySeconds: runway,
    heldSince: seat.claimedAt || seat.lastUpdateTime || Math.floor(Date.now() / 1000),
    isInCooldown,
    cooldownMinutesRemaining,
    earningsKind: earnings.kind,
    earningsSymbol: earnings.symbol,
    earningsDecimals: earnings.decimals,
  };
}

/**
 * Group seats by coin/pool for display in CoinSeatsCard
 * 
 * @param seats Array of raw seat data from API
 * @param liveAvgEarningsUsd Optional live earnings data for more accurate calculations
 * @param ethPriceUsd Optional ETH price in USD for converting totalRewardsEth (V1 pools)
 * @param tokenPrices Optional map of token address -> USD price (V2 yield tokens)
 * @param options.includeForfeited Surface tax-depleted seats with
 *   `isForfeited=true` instead of dropping them. Required by the lobby
 *   manage modal so its row count matches the header chip — see
 *   `transformToSeatPosition` for why.
 */
export function groupSeatsByCoin(
  seats: RawSeatData[],
  liveAvgEarningsUsd?: bigint,
  ethPriceUsd?: number,
  tokenPrices?: Record<string, number>,
  rewards7dMap?: Map<string, string>,
  options?: { includeForfeited?: boolean },
): CoinSeatsData[] {
  const grouped = new Map<string, CoinSeatsData>();
  
  for (const seat of seats) {
    const poolId = seat.pool?.id || seat.poolId || '';
    if (!poolId) continue;
    
    const position = transformToSeatPosition(seat, liveAvgEarningsUsd, ethPriceUsd, tokenPrices, rewards7dMap, options);

    if (!grouped.has(poolId)) {
      grouped.set(poolId, {
        poolId,
        poolName: seat.pool?.name || `Pool ${poolId.slice(0, 8)}`,
        poolSymbol: seat.pool?.symbol || seat.pool?.name || poolId.slice(0, 6).toUpperCase(),
        poolImageUrl: seat.pool?.imageUrl,
        memecoinAddress: seat.pool?.memecoinAddress,
        managerAddress: seat.pool?.managerAddress,
        floorPrice: safeBigInt(seat.pool?.floorPrice),
        activeSeatCount: seat.pool?.activeSeatCount,
        isGraduated: seat.pool?.isGraduated,
        isV2Grid: seat.pool?.isV2Grid,
        earningsKind: position?.earningsKind,
        earningsSymbol: position?.earningsSymbol,
        earningsDecimals: position?.earningsDecimals,
        taxToken: seat.pool?.taxToken,
        taxTokenDecimals: seat.pool?.taxTokenDecimals,
        totalSeats: seat.pool?.totalSeats,
        seats: [],
      });
    }
    
    if (position) grouped.get(poolId)!.seats.push(position);
  }
  
  return Array.from(grouped.values()).filter(group => group.seats.length > 0);
}
